import React, { forwardRef, memo } from 'react';
import { useFragment, graphql } from 'react-relay';
import { Box, xcss } from '@atlaskit/primitives';
import { fg } from '@atlassian/jira-feature-gating';
import { LabelsInlineEditViewWithIsEditable } from '@atlassian/jira-issue-field-labels-inline-edit-view-full/src/ui/labels/index.tsx';
import { KNOWN_COLUMN_TYPES } from '@atlassian/jira-issue-table-common/src/constants.tsx';
import type { LabelsCell$key } from '@atlassian/jira-relay/src/__generated__/LabelsCell.graphql';
import type { LabelsCell_LabelsInlineEditCell$key } from '@atlassian/jira-relay/src/__generated__/LabelsCell_LabelsInlineEditCell.graphql';
import {
	useIsDensityFull,
	useIsInlineEditingExtendedFieldSupportEnabled,
	useIsSingleLineRowHeightEnabled,
} from '../../../../controllers/features/selectors.tsx';
import { useLazyLoadEditability } from '../../../../services/lazy-editability-loader/index.tsx';
import { INLINE_EDIT_ANALYTICS_ATTRIBUTES } from '../common/inline-edit-analytics-attributes.tsx';
import { DUPLICATE_FIELD_EDIT_WIDTH } from '../../../constants.tsx';

export type Props = {
	fieldRef: LabelsCell$key;
	issueSearchBaseUrl: string;
	isFirstField?: boolean;
	hasDuplicates?: boolean;
};

export const LabelsCell = memo<Props>(
	({ fieldRef, issueSearchBaseUrl, isFirstField, hasDuplicates }: Props) => {
		const data = useFragment<LabelsCell$key>(
			graphql`
				fragment LabelsCell on JiraLabelsField
				@argumentDefinitions(isInlineEditingEnabled: { type: "Boolean!" }) {
					...LabelsCell_LabelsInlineEditCell
						@arguments(isInlineEditingEnabled: $isInlineEditingEnabled)
				}
			`,
			fieldRef,
		);

		const isDensityFull = useIsDensityFull();
		const isSingleLineRowHeightEnabled = useIsSingleLineRowHeightEnabled();

		return (
			<LabelsInlineEditCell
				fieldRef={data}
				issueSearchBaseUrl={issueSearchBaseUrl}
				isTruncated={!isDensityFull}
				isHoverPopoverEnabled={isSingleLineRowHeightEnabled}
				isFirstField={isFirstField}
				hasDuplicates={hasDuplicates}
			/>
		);
	},
);

type LabelsInlineEditCellProps = {
	fieldRef: LabelsCell_LabelsInlineEditCell$key;
	issueSearchBaseUrl: string;
	isTruncated: boolean;
	/** Whether the menu should use a portal, and where it should attach */
	menuPortalTarget?: HTMLElement;
	isHoverPopoverEnabled: boolean;
	isFirstField?: boolean;
	hasDuplicates?: boolean;
};

export const LabelsInlineEditCell = ({
	fieldRef,
	issueSearchBaseUrl,
	isTruncated,
	menuPortalTarget,
	isHoverPopoverEnabled,
	isFirstField,
	hasDuplicates,
}: LabelsInlineEditCellProps) => {
	const data = useFragment<LabelsCell_LabelsInlineEditCell$key>(
		graphql`
			fragment LabelsCell_LabelsInlineEditCell on JiraLabelsField
			@argumentDefinitions(isInlineEditingEnabled: { type: "Boolean!" }) {
				id
				fieldId
				lazyIsEditableInIssueView @include(if: $isInlineEditingEnabled)
				...labels_issueFieldLabelsInlineEditViewFull_LabelsInlineEditViewWithIsEditable_fragmentRef
			}
		`,
		fieldRef,
	);

	const isLabelsSystemField = data.fieldId === KNOWN_COLUMN_TYPES.LABELS;
	const isInlineEditingExtendedFieldSupportEnabled =
		useIsInlineEditingExtendedFieldSupportEnabled();
	const isInlineEditingSupported =
		isLabelsSystemField || isInlineEditingExtendedFieldSupportEnabled;
	// If the property does not exist in fragment data then we know it has been conditionally excluded because inline
	// editing is disabled. In this case we explicitly set isEditable to false to bypass the lazy editability query.
	const isEditable =
		'lazyIsEditableInIssueView' in data && isInlineEditingSupported
			? data.lazyIsEditableInIssueView
			: false;
	useLazyLoadEditability(data.id, isEditable);

	if (hasDuplicates && isHoverPopoverEnabled) {
		return (
			<LabelsInlineEditViewWithIsEditable
				attributes={INLINE_EDIT_ANALYTICS_ATTRIBUTES}
				spacing="default"
				baseSearchUrl={issueSearchBaseUrl}
				editViewPopup
				editViewPopupAlignBlock="start"
				editViewPopupWidth={DUPLICATE_FIELD_EDIT_WIDTH}
				// @ts-expect-error: to unblock React 18.2.0 -> 18.3.1 version bump
				editViewPopupTarget={isFirstField ? CustomTarget : undefined}
				fragmentRef={data}
				isTruncated={isTruncated}
				isEditable={isEditable ?? false}
				menuPosition={isFirstField ? 'fixed' : 'absolute'}
				menuPortalTarget={menuPortalTarget}
				readViewFitContainerHeight
				isHoverPopoverEnabled={isFirstField && isHoverPopoverEnabled}
			/>
		);
	}

	return (
		<LabelsInlineEditViewWithIsEditable
			attributes={INLINE_EDIT_ANALYTICS_ATTRIBUTES}
			spacing="default"
			baseSearchUrl={issueSearchBaseUrl}
			editViewPopup
			editViewPopupAlignBlock="center"
			fragmentRef={data}
			isTruncated={isTruncated}
			isEditable={isEditable ?? false}
			menuPosition="fixed"
			menuPortalTarget={menuPortalTarget}
			readViewFitContainerHeight
			isHoverPopoverEnabled={isHoverPopoverEnabled}
		/>
	);
};

const CustomTarget = forwardRef<HTMLDivElement, {}>((_, ref) => {
	return (
		<Box
			xcss={fg('nin_issue_table_rtl_fix') ? customTargetStyles : customTargetStylesOld}
			ref={ref}
		/>
	);
});

const customTargetStylesOld = xcss({
	width: 'space.0',
	position: 'absolute',
	// eslint-disable-next-line @atlaskit/design-system/no-physical-properties
	top: 'space.0',
});

const customTargetStyles = xcss({
	width: 'space.0',
	position: 'absolute',
	insetBlockStart: 'space.0',
});
